import { usePostHog } from '~/composables/usePostHog';

type CookieYesConsent = {
  categories?: Record<string, boolean>;
};

type OneTrustApi = {
  OnConsentChanged?: (callback: () => void) => void;
};

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    getCkyConsent?: () => CookieYesConsent;
    OneTrust?: OneTrustApi;
    OnetrustActiveGroups?: string;
    OptanonWrapper?: () => void;
  }
}

const COOKIEYES_ANALYTICS_CATEGORY = 'analytics';
const ONETRUST_ANALYTICS_GROUP = 'C0002';
const COOKIEYES_CONSENT_EVENT = 'cookieyes_consent_update';

const VENDOR_POLL_INTERVAL_MS = 200;
const VENDOR_POLL_TIMEOUT_MS = 10_000;

type ConsentReader = () => boolean;
type ConsentSubscriber = (onChange: () => void) => void;

type VendorAdapter = {
  read: ConsentReader;
  subscribe: ConsentSubscriber;
};

let cleanupFns: (() => void)[] = [];
let pollTimer: ReturnType<typeof setInterval> | null = null;

const detectVendorAdapter = (): VendorAdapter | null => {
  // eslint-disable-next-line unicorn/prefer-global-this
  const w: Window = window;

  if (typeof w.getCkyConsent === 'function') {
    return {
      read: () =>
        w.getCkyConsent?.()?.categories?.[COOKIEYES_ANALYTICS_CATEGORY] ===
        true,
      subscribe: (onChange) => {
        document.addEventListener(COOKIEYES_CONSENT_EVENT, onChange);
        cleanupFns.push(() =>
          document.removeEventListener(COOKIEYES_CONSENT_EVENT, onChange),
        );
      },
    };
  }

  if (w.OneTrust || w.OnetrustActiveGroups !== undefined) {
    return {
      read: () =>
        (w.OnetrustActiveGroups ?? '').includes(
          `,${ONETRUST_ANALYTICS_GROUP},`,
        ),
      subscribe: (onChange) => {
        if (typeof w.OneTrust?.OnConsentChanged === 'function') {
          w.OneTrust.OnConsentChanged(onChange);
          return;
        }
        const previous = w.OptanonWrapper;
        w.OptanonWrapper = () => {
          previous?.();
          onChange();
        };
        cleanupFns.push(() => {
          w.OptanonWrapper = previous;
        });
      },
    };
  }

  return null;
};

const clearPollTimer = (): void => {
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

const teardown = (): void => {
  for (const fn of cleanupFns) {
    fn();
  }
  cleanupFns = [];
  clearPollTimer();
};

export const useCookieConsent = () => {
  const { setConsent } = usePostHog();

  const bindAdapter = (adapter: VendorAdapter): void => {
    const apply = () => setConsent(adapter.read());
    apply();
    adapter.subscribe(apply);
  };

  const syncPostHogConsent = (): void => {
    if (import.meta.server) return;

    teardown();

    const immediate = detectVendorAdapter();
    if (immediate) {
      bindAdapter(immediate);
      return;
    }

    let elapsed = 0;
    pollTimer = setInterval(() => {
      elapsed += VENDOR_POLL_INTERVAL_MS;
      const adapter = detectVendorAdapter();
      if (adapter) {
        clearPollTimer();
        bindAdapter(adapter);
        return;
      }
      if (elapsed >= VENDOR_POLL_TIMEOUT_MS) {
        clearPollTimer();
        if (import.meta.dev) {
          // eslint-disable-next-line no-console
          console.warn(
            '[CookieConsent] No cookie vendor detected; PostHog stays opted out.',
          );
        }
      }
    }, VENDOR_POLL_INTERVAL_MS);
  };

  return { syncPostHogConsent, teardown };
};
